/**
 * useTenantResolver - Resolve tenant by email (join existing or create new) during registration.
 * MODULE: core/auth/frontend/hooks/useTenantResolver.ts
 */
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import { useQuery } from '@tanstack/react-query';
import type { TenantResolution } from '../../contracts/auth.types';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

async function resolveTenant(email: string): Promise<TenantResolution> {
  const res = await fetch(`${API_BASE}/resolve-tenant`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Failed to resolve tenant');
  const data = await res.json();
  return {
    action: data.action,
    existingTenant: data.existing_tenant
      ? {
          id: data.existing_tenant.id,
          name: data.existing_tenant.name,
          domain: data.existing_tenant.domain,
          logoUrl: data.existing_tenant.logo_url,
        }
      : undefined,
  };
}

export function useTenantResolver(email: string) {
  const query = useQuery({
    queryKey: ['tenant-resolver', email],
    queryFn: () => resolveTenant(email),
    enabled: !!email && email.includes('@'),
    staleTime: 5 * 60 * 1000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
